/**
 * Real-time Collaboration Hook
 * ============================
 * WebSocket hook for real-time transaction updates.
 * Enables multiple accountants to collaborate on the same case.
 * 
 * Features:
 * - Live transaction updates
 * - Presence indicators (who's viewing)
 * - Conflict detection for concurrent edits
 */

import { useEffect, useState, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface CollaborationUser {
  userId: string;
  userName: string;
  role: string;
  connectedAt: string;
  lastActivity: string;
}

interface TransactionUpdate {
  type: 'transaction_updated' | 'transaction_reclassified' | 'transaction_flagged' | 'transaction_signoff' | 'user_joined' | 'user_left';
  transactionId?: string;
  reportId: string;
  userId: string;
  userName: string;
  timestamp: string;
  data?: any;
}

interface UseCollaborationOptions {
  reportId: string;
  userId?: string;
  userName?: string;
  onTransactionUpdate?: (update: TransactionUpdate) => void;
  enabled?: boolean;
}

interface UseCollaborationReturn {
  isConnected: boolean;
  activeUsers: CollaborationUser[];
  lastUpdate: TransactionUpdate | null;
  sendUpdate: (update: Partial<TransactionUpdate>) => void;
  reconnect: () => void;
}

export function useCollaboration({
  reportId,
  userId = 'anonymous',
  userName = 'Anonymous User',
  onTransactionUpdate,
  enabled = true
}: UseCollaborationOptions): UseCollaborationReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState<CollaborationUser[]>([]);
  const [lastUpdate, setLastUpdate] = useState<TransactionUpdate | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!enabled || !reportId) return;

    // Build WebSocket URL
    const wsProtocol = BACKEND_URL?.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL?.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/report/${reportId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log(`🔌 Collaboration connected for report: ${reportId}`);
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Send join message
        ws.send(JSON.stringify({
          type: 'user_joined',
          reportId,
          userId,
          userName,
          timestamp: new Date().toISOString()
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle different message types
          switch (message.type) {
            case 'active_users':
              setActiveUsers(message.users || []);
              break;
            
            case 'user_joined':
              setActiveUsers(prev => {
                const existing = prev.find(u => u.userId === message.userId);
                if (existing) return prev;
                return [...prev, {
                  userId: message.userId,
                  userName: message.userName,
                  role: message.role || 'accountant',
                  connectedAt: message.timestamp,
                  lastActivity: message.timestamp
                }];
              });
              break;
            
            case 'user_left':
              setActiveUsers(prev => prev.filter(u => u.userId !== message.userId));
              break;
            
            case 'transaction_updated':
            case 'transaction_reclassified':
            case 'transaction_flagged':
            case 'transaction_signoff':
              setLastUpdate(message);
              onTransactionUpdate?.(message);
              break;
            
            case 'pong':
              // Heartbeat response
              break;
            
            default:
              console.log('Unknown collaboration message:', message);
          }
        } catch (err) {
          console.error('Error parsing collaboration message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('Collaboration WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log(`🔌 Collaboration disconnected (code: ${event.code})`);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        }
      };
    } catch (err) {
      console.error('Failed to create collaboration WebSocket:', err);
    }
  }, [enabled, reportId, userId, userName, onTransactionUpdate]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      // Send leave message before closing
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'user_left',
          reportId,
          userId,
          userName,
          timestamp: new Date().toISOString()
        }));
      }
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setActiveUsers([]);
  }, [reportId, userId, userName]);

  const sendUpdate = useCallback((update: Partial<TransactionUpdate>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        ...update,
        reportId,
        userId,
        userName,
        timestamp: new Date().toISOString()
      }));
    }
  }, [reportId, userId, userName]);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, disconnect]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => clearInterval(heartbeat);
  }, [isConnected]);

  return {
    isConnected,
    activeUsers,
    lastUpdate,
    sendUpdate,
    reconnect
  };
}

/**
 * Presence Indicator Component
 * Shows who else is currently viewing the same report
 */
interface PresenceIndicatorProps {
  activeUsers: CollaborationUser[];
  currentUserId?: string;
}

export function PresenceIndicator({ activeUsers, currentUserId }: PresenceIndicatorProps) {
  const otherUsers = activeUsers.filter(u => u.userId !== currentUserId);
  
  if (otherUsers.length === 0) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
      <div className="flex -space-x-2">
        {otherUsers.slice(0, 3).map((user, index) => (
          <div
            key={user.userId}
            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2"
            title={user.userName}
            style={{ zIndex: 3 - index, backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', borderColor: 'var(--color-bg-surface)' }}
          >
            {user.userName.charAt(0).toUpperCase()}
          </div>
        ))}
        {otherUsers.length > 3 && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2" style={{ backgroundColor: 'var(--color-text-muted)', color: 'var(--color-on-brand)', borderColor: 'var(--color-bg-surface)' }}>
            +{otherUsers.length - 3}
          </div>
        )}
      </div>
      <span className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
        {otherUsers.length === 1
          ? `${otherUsers[0].userName} is viewing`
          : `${otherUsers.length} others viewing`}
      </span>
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--color-verified)' }}></span>
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: 'var(--color-verified)' }}></span>
      </span>
    </div>
  );
}

export default useCollaboration;
